import { isBoolean, isNumber } from 'lodash';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import mean from 'lodash/mean';
import sum from 'lodash/sum';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import { AutopopulateResponse } from '../../../services/SurveyService';
import { Version } from '../../../util/Versions';
import { reportErrorToBugsnagDirect } from '../../../util/bugsnagReporting';
import { CustomMessages } from '../Components/SurveyBuilder/types';
import ChoiceOptions from './ChoiceOptions';
import { dateParser } from './DateTimeParsers';
import ExtensionCollection from './ExtensionCollection';
import QuestionSearch from './QuestionSearch';
import QuestionnaireItemUpdater from './QuestionnaireMapper/QuestionnaireItemUpdater';
import QuestionnaireUpdater from './QuestionnaireUpdater';
import SurveyDependencies, {
  DependencyCheckResult,
} from './SurveyDependencies';
import SurveyItem from './SurveyItem';
import SurveyItemAutopopulation from './SurveyItemAutopopulation';
import SurveyResponse, {
  ResponseItem,
  ResponseUpdate,
  ResponseValueTypes,
  buildInstanceSegment,
  parsePathSegment,
} from './SurveyResponse';
import SurveyTree from './SurveyTree';
import ValueSet, { Coding, SurveyValueSetPurpose } from './ValueSet';
import ValueSets from './ValueSets/index';
import fhirTypeCheck from './fhirTypeCheck';
import questionnaireUtils from './questionnaireUtils';
import surveyUrls from './surveyUrls';

export type SurveyStatus = 'draft' | 'active' | 'retired';

export type ScoreResult = {
  sum: number | null;
  avg: number | null;
};

export type CodeInputValue = {
  display: string;
  code?: string;
  numericValue: number | null;
  description?: string;
};

export type CreateValueSetInput = {
  name: string;
  system?: string;
  codes: CodeInputValue[];
  enableNumericValues: boolean;
  purpose: SurveyValueSetPurpose;
};

export type ImportSurveyQuestionResult = {
  status: 'success' | 'item-already-exists' | 'invalid-item' | 'unkown-error';
  updatedSurvey?: Survey;
};

export const allCustomMessageTypes: CustomMessages[] = [
  'submission-header',
  'submission-content',
  'submission-accept',
  'submission-reject',
];

export type AutopopulationWithLinkId = {
  itemLinkId: string;
  autopopulation: SurveyItemAutopopulation;
};

/**
 * Addresses one instance of a repeating group.  Nested repeating groups are
 * addressed with a chain of these (outermost group first).
 */
export type RepeatingGroupInstanceRef = {
  groupLink: string;
  instanceIndex: number;
};

/**
 * Wrapper for data to display current page/total pages
 */
export class PaginationReference {
  _currentPageIndex: number;
  _totalPages: number;

  constructor(currentPageIndex: number, totalPages: number) {
    this._currentPageIndex = currentPageIndex;
    this._totalPages = totalPages;
  }

  /**
   * the current survey page index
   */
  get currentPageIndex(): number {
    return this._currentPageIndex;
  }

  /**
   * the total survey pages
   */
  get totalPages(): number {
    return this._totalPages;
  }
}

/**
 * Organizes paging data
 *
 * @class SurveyPaginationIndex
 */
export class SurveyPagination {
  _prevItem: SurveyItem;
  _curItem: SurveyItem;
  _nextItem: SurveyItem;

  constructor(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    prevItem: SurveyItem = null,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    curItem: SurveyItem = null,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    nextItem: SurveyItem = null,
  ) {
    this._prevItem = prevItem;
    this._curItem = curItem;
    this._nextItem = nextItem;
  }

  /**
   * Prev page may exist, but may be disabled because it was a checkpoint page.  Checkpoint pages do not allow the user to go back and edit.
   * @param isReadOnly - Is the survey in read only mode
   */
  getPrevPageDisabled(isReadOnly: boolean): boolean {
    if (!this.hasPrev) return true;

    // readonly mode should allow free navigation
    if (isReadOnly) return false;

    // checkpoint pages don't allow the user to navigate backwards
    return this._prevItem.isCheckpointPage;
  }

  get prevLink(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._prevItem ? this._prevItem.link : null;
  }

  get hasPrev(): boolean {
    return !!this.prevLink;
  }

  get curLink(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._curItem ? this._curItem.link : null;
  }

  /**
   * Whether or not this page should be enforced as a checkpoint and dissallow backwards navigation from this point forward
   * @param isReadOnly - Is the survey in read only mode
   */
  shouldEnforcePageAsCheckpoint(isReadOnly: boolean): boolean {
    // if no next page, checkpoint cannot be enforced
    if (!this.hasNext) return false;

    // readonly mode should allow free navigation
    if (isReadOnly) return false;

    return this._curItem.isCheckpointPage;
  }

  get nextLink(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this._nextItem ? this._nextItem.link : null;
  }

  get hasNext(): boolean {
    return !!this.nextLink;
  }
}

export class FieldValueUpdate {
  _fieldLink: string;
  _fieldValueType: ResponseValueTypes;
  _fieldValue: string;

  constructor(
    fieldLink: string,
    fieldValueType: ResponseValueTypes,
    fieldValue: any,
  ) {
    this._fieldLink = fieldLink;
    this._fieldValueType = fieldValueType;
    this._fieldValue = fieldValue;
  }

  get fieldLink(): string {
    return this._fieldLink;
  }

  get fieldValueType(): ResponseValueTypes {
    return this._fieldValueType;
  }

  get fieldValue(): any {
    return this._fieldValue;
  }
}

/**
 * This provides an immutable wrapper around a fhir questionnaire resource type providing utility methods.
 */
export default class Survey {
  _fhirQuestionnaire: fhir.Questionnaire;
  _name: string;
  _title: string;
  _description: string;
  _valueSets: ValueSets;
  _items: SurveyItem[];
  _surveyTree: SurveyTree;
  _choiceOptionsCache: any;
  _questionSearch: QuestionSearch;
  _surveyDependencies: SurveyDependencies;

  constructor(fhirQuestionnaire: fhir.Questionnaire) {
    this._fhirQuestionnaire = fhirQuestionnaire;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._name = get<fhir.Questionnaire, 'name'>(fhirQuestionnaire, 'name');
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._title = get<fhir.Questionnaire, 'title'>(fhirQuestionnaire, 'title');
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._description = get<fhir.Questionnaire, 'description'>(
      fhirQuestionnaire,
      'description',
    );
    this._valueSets = new ValueSets(fhirQuestionnaire);
    this._items = get<fhir.Questionnaire, 'item', fhir.QuestionnaireItem[]>(
      fhirQuestionnaire,
      'item',
      [] as fhir.QuestionnaireItem[],
    ).map(
      (questionnaireItem: fhir.QuestionnaireItem) =>
        new SurveyItem(questionnaireItem, true),
    );
    this._surveyTree = new SurveyTree(this._items);
    this._questionSearch = new QuestionSearch(this._surveyTree);
    this._choiceOptionsCache = {};
    this._surveyDependencies = new SurveyDependencies(
      this._surveyTree.allItems,
    );
  }

  /**
   * Returns the API generated ID for the underlying resource.  Note that this may be null if not ever posted to the API
   */
  get id(): string {
    return get<fhir.Questionnaire, 'id', string>(
      this._fhirQuestionnaire,
      'id',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
  }

  get status(): SurveyStatus {
    return get<fhir.Questionnaire, 'status', string>(
      this._fhirQuestionnaire,
      'status',
      'draft',
    ) as SurveyStatus;
  }

  get name(): string {
    return this._name;
  }

  get title(): string {
    return this._title;
  }

  get description(): string {
    return this._description;
  }

  get valueSets(): ValueSets {
    return this._valueSets;
  }

  /**
   * All survey items in tree structure
   */
  get surveyItems(): SurveyItem[] {
    return this._items;
  }

  /**
   * All survey items in a flattened/array structure
   */
  get allItems(): SurveyItem[] {
    return this._surveyTree.allItems;
  }

  get questionnaire(): fhir.Questionnaire {
    return this._fhirQuestionnaire;
  }

  get questionSearch(): QuestionSearch {
    return this._questionSearch;
  }

  get allSurveyLinks(): string[] {
    return this._surveyTree.allLinks;
  }

  get isInternalSurvey(): boolean {
    return questionnaireUtils.isInternalSurvey(this._fhirQuestionnaire);
  }

  get allowsResponseResubmission(): boolean {
    return questionnaireUtils.allowsResponseResubmission(
      this._fhirQuestionnaire,
    );
  }

  get allowsMemberResponsePdfVisibility(): boolean {
    return questionnaireUtils.hasCustomVendorId(this._fhirQuestionnaire);
  }

  get primarySurveyId(): string | null {
    return questionnaireUtils.getPrimarySurveyId(this._fhirQuestionnaire);
  }

  get hasPrimarySurveyId(): boolean {
    return this.primarySurveyId !== null;
  }

  get version(): Version {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return new Version(this._fhirQuestionnaire.version);
  }

  get hasAutopopulationConfigured(): boolean {
    return !!this.autopopulationConfig.length;
  }

  get autopopulationConfig(): AutopopulationWithLinkId[] {
    return this.allItems
      .filter(_ => _.hasAutopopulationConfigured)
      .map<AutopopulationWithLinkId>(_ => ({
        itemLinkId: _.link,
        autopopulation: _.autopopulation,
      }));
  }

  /**
   * Does the survey contain an item with the link
   */
  doesContainItem(linkId: string) {
    return this._surveyTree.getSurveyItemWithLink(linkId) !== null;
  }

  getSurveyItemWithLinkId(linkId: string): SurveyItem {
    return this._surveyTree.getSurveyItemWithLink(linkId);
  }

  /**
   * Is the survey empty and has no pages (for paginated survey) or root-level controls (for non-paginated survey)
   *
   * @readonly
   * @type {boolean}
   * @memberof Survey
   */
  get hasNoItems(): boolean {
    return this._items.length === 0;
  }

  /**
   * Retrieves all survey items that repeat.  These are the items which response values are arrays
   *
   * @readonly
   * @type {string[]}
   * @memberof Survey
   */
  get allRepeatingItems(): string[] {
    const allSurveyLinks = this._surveyTree.allLinks;
    const repeatingGroupsEnabled = this.isRepeatingGroupsEnabled();
    return allSurveyLinks.filter(surveyItemLink => {
      const surveyItem = this._surveyTree.getSurveyItemWithLink(surveyItemLink);
      // when the survey opts into repeating groups, repeats on a group means
      // repeated instances, not a multi-value answer.  Without the opt-in,
      // legacy behavior is preserved exactly.
      return (
        surveyItem.repeats &&
        !(repeatingGroupsEnabled && surveyItem.isRepeatingGroup)
      );
    });
  }

  /**
   * Retrieves the linkIds of all repeating groups (group items with
   * repeats = true).  Their responses are repeated same-linkId items, one per
   * answered instance.
   *
   * Empty unless the survey opts in via the enable-repeating-groups
   * extension: instance semantics (parse containers, per-instance form value
   * arrays) must not activate for surveys the legacy Group renderer still
   * owns, or formik initial values would not match the flat field names it
   * binds.
   *
   * @readonly
   * @type {string[]}
   * @memberof Survey
   */
  get allRepeatingGroupItems(): string[] {
    if (!this.isRepeatingGroupsEnabled()) return [];

    const allSurveyLinks = this._surveyTree.allLinks;
    return allSurveyLinks.filter(surveyItemLink => {
      const surveyItem = this._surveyTree.getSurveyItemWithLink(surveyItemLink);
      return surveyItem.isRepeatingGroup;
    });
  }

  /**
   * Is this survey divided into multiple pages of individual forms
   *
   * @readonly
   * @type {boolean}
   * @memberof Survey
   */
  get isPaginatedSurvey(): boolean {
    return (
      this._items.length > 0 &&
      this._items.every(item => item.isSurveyPageGroup)
    );
  }

  /**
   * Gets the page items of this survey.  If this is not a paginated survye, an empty array is returned
   */
  get pageItems(): SurveyItem[] {
    if (!this.isPaginatedSurvey) return [];

    return this._items.filter(item => item.isSurveyPageGroup);
  }

  /**
   * The link of the last page of the survey.  Null if not a paginated survey or no pages exist
   *
   * @readonly
   * @type {string}
   * @memberof Survey
   */
  get lastPageLink(): string {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isPaginatedSurvey || this.hasNoItems) return null;

    const lastIndex = this._items.length - 1;
    return this._items[lastIndex].link;
  }

  /**
   * Given the specified page link, returns a number between 0 and 1 indicating the
   * current progress of the survey.  1 = complete, 0 = not started
   */
  getSurveyProgress(currentPageLink: string): number {
    if (!this.isPaginatedSurvey) return 0;

    const rootItems = this.surveyItems.map(surveyItem => surveyItem.link);

    const curIndex = rootItems.indexOf(currentPageLink);

    if (curIndex === -1) return 0;

    return (curIndex + 1) / rootItems.length;
  }

  /**
   * Gets the reference pagination (X page out of Y pages)
   */
  getPaginationReferenceForPage(linkId: string): PaginationReference {
    if (!this.isPaginatedSurvey) {
      console.error(
        'Tried to get pagination reference for a non-paginated survey',
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    const pageIndex = this._items.findIndex(item => item.link === linkId);
    if (pageIndex === -1) {
      console.error(
        `Tried to retrieve pagination for a non-existent page with linkId "${linkId}"`,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    return new PaginationReference(pageIndex, this._items.length);
  }

  /**
   * Retrieves the pagination reference for a given link
   *
   * @param {string} curPageLink
   * @returns {SurveyPagination}
   * @memberof Survey
   */
  getSurveyPaginationForLink(
    curPageLink: string,
    surveyResponse: SurveyResponse,
  ): SurveyPagination {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isPaginatedSurvey) return null;

    const rootItems = this.surveyItems.map(surveyItem => surveyItem.link);

    const curIndex = rootItems.indexOf(curPageLink);

    const copied = [...rootItems];
    const prevRootItems = copied.splice(0, curIndex);
    const remainingRootItems = copied.splice(1);

    const getIsPageEnabled = (pageLink: string) => {
      const targetItem = this._surveyTree.getSurveyItemWithLink(pageLink);
      if (!targetItem) return false; // should never happen

      // checkpoint pages should always be "enabled" to ensure the user is always presented with checkpoint pages and prompts
      if (targetItem.isCheckpointPage) return true;

      // check if page is logically enabled or any page items are logically enabled
      return this.getAreAnySubItemsEnabled(pageLink, surveyResponse);
    };

    const firstPrevEnabledRoot = prevRootItems.reverse().find(getIsPageEnabled);

    const firstNextEnabledRoot = remainingRootItems.find(getIsPageEnabled);

    const prevItem = firstPrevEnabledRoot
      ? this._surveyTree.getSurveyItemWithLink(firstPrevEnabledRoot)
      : null;
    const currentLinkId = rootItems[curIndex];
    const currentItem = this._surveyTree.getSurveyItemWithLink(currentLinkId);
    const nextItem = firstNextEnabledRoot
      ? this._surveyTree.getSurveyItemWithLink(firstNextEnabledRoot)
      : null;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return new SurveyPagination(prevItem, currentItem, nextItem);
  }

  /**
   * Recursively checks if all sub items are disabled based on logic enablement
   * @param link - the root item
   * @param surveyResponse
   */
  getAreAnySubItemsEnabled(
    link: string,
    surveyResponse: SurveyResponse,
  ): boolean {
    const isTargetLinkEnabled = this.getIsItemEnabled(link, surveyResponse);

    if (!isTargetLinkEnabled) {
      // if the root is disabled, it doesn't matter if the children are enabled/disabled
      return false;
    }

    const childrenLinks = this._surveyTree.getDirectChildrenLinks(link);
    if (!childrenLinks.length) {
      // target link is enabled and there are no children
      return true;
    }

    // the root can be enabled, but if all children are disabled, there still won't be any input controls
    return childrenLinks.some(childLink =>
      this.getAreAnySubItemsEnabled(childLink, surveyResponse),
    );
  }

  /**
   * Retrieves the initial pagination for the survey
   *
   * @returns {SurveyPagination}
   * @memberof Survey
   */
  getDefaultSurveyPagination(
    surveyResponse = this.defaultResponseWithInitialValues(),
  ): SurveyPagination {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isPaginatedSurvey) return null;

    const initialItemLink = (this.surveyItems[0] || ({} as any)).link;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!initialItemLink) return null;

    return this.getSurveyPaginationForLink(initialItemLink, surveyResponse);
  }

  /**
   * Retrieves the survey page with the given link
   *
   * @param {string} link
   * @returns {SurveyItem}
   * @memberof Survey
   */
  getSurveyPageWithLink(link: string): SurveyItem {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!this.isPaginatedSurvey) return null;

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this.surveyItems.find(surveyItem => surveyItem.link === link);
  }

  /**
   * Retrieves the last root item on the target page.  Returns null if no items on page
   */
  getLastItemOnPage(pageLink: string): SurveyItem {
    const pageItem = this._surveyTree.getSurveyItemWithLink(pageLink);

    if (!pageItem) {
      console.error(
        `Attempted to retrieve the last survey item on a non-existent survey page with linkId: "${pageLink}"`,
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    if (!pageItem.hasSubItems) return null;

    const lastItemindex = pageItem.subItems.length - 1;
    return pageItem.subItems[lastItemindex];
  }

  /**
   * Finds the field's position in the survey for easier response updating
   *
   * @param {FieldValueUpdate[]} fieldUpdates
   * @returns {ResponseUpdate[]}
   * @memberof Survey
   */
  /**
   * Resolves a form field name to a survey response update path.  Plain
   * field names are bare linkIds resolved through the survey tree, exactly
   * as before.  Fields inside repeating group instances have qualified names
   * ("sisters[1].sister-age"); the tree path of the leaf is resolved and the
   * repeating group segments get their instance index applied.
   */
  fieldNameToUpdatePath(fieldName: string): string[] {
    if (!fieldName.includes('[')) {
      return this._surveyTree.getPathOfItem(fieldName);
    }

    const nameSegments = fieldName.split('.').map(parsePathSegment);
    const leafLink = nameSegments[nameSegments.length - 1].link;
    const instanceIndexByLink: { [link: string]: number } = {};
    nameSegments.forEach(({ link, instanceIndex }) => {
      if (instanceIndex !== null) {
        instanceIndexByLink[link] = instanceIndex;
      }
    });

    return this._surveyTree
      .getPathOfItem(leafLink)
      .map(pathSegment =>
        pathSegment in instanceIndexByLink
          ? buildInstanceSegment(pathSegment, instanceIndexByLink[pathSegment])
          : pathSegment,
      );
  }

  /**
   * Extracts the bare linkId from a (possibly instance-qualified) field name
   */
  private _fieldNameToLeafLink(fieldName: string): string {
    if (!fieldName.includes('[')) return fieldName;

    const nameSegments = fieldName.split('.');
    return parsePathSegment(nameSegments[nameSegments.length - 1]).link;
  }

  fieldUpdatesToResponseUpdates(
    fieldUpdates: FieldValueUpdate[],
    ignoreNonexistentFields: boolean = false,
  ): ResponseUpdate[] {
    return fieldUpdates
      .filter(_ => {
        if (!ignoreNonexistentFields) return true;

        return this._surveyTree.allLinks.includes(
          this._fieldNameToLeafLink(_.fieldLink),
        );
      })
      .map(fieldUpdate => {
        const fieldPath = this.fieldNameToUpdatePath(fieldUpdate.fieldLink);

        return new ResponseUpdate(
          fieldPath,
          fieldUpdate.fieldValueType,
          fieldUpdate.fieldValue,
        );
      });
  }

  /**
   * Determines if the specified field like should be enabled based on the given survey response
   * @param {string} fieldLink - The target field
   * @param {SurveyResponse} surveyResponse - The current state of the survey response
   * @param {Object|Object[]} [repeatingGroupInstance] - Set when the field renders inside a repeating group instance.  For nested repeating groups, pass the full ancestor chain (outermost first).  enableWhen conditions referencing questions inside the same group(s) resolve within those instances; references to questions outside are unchanged.
   * @returns {Boolean}
   */
  getIsItemEnabled(
    fieldLink: string,
    surveyResponse: SurveyResponse,
    repeatingGroupInstance?:
      | RepeatingGroupInstanceRef
      | RepeatingGroupInstanceRef[],
  ): boolean {
    const targetSurveyItem = this._surveyTree.getSurveyItemWithLink(fieldLink);

    if (targetSurveyItem === null || !targetSurveyItem.itemEnablement) {
      return true;
    }

    const itemEnablement = targetSurveyItem.itemEnablement;

    if (!itemEnablement.hasEnablementCriteria) return true;

    const instanceRefs: RepeatingGroupInstanceRef[] = !repeatingGroupInstance
      ? []
      : isArray(repeatingGroupInstance)
      ? repeatingGroupInstance
      : [repeatingGroupInstance];
    const instanceIndexByGroupLink: { [groupLink: string]: number } = {};
    instanceRefs.forEach(({ groupLink, instanceIndex }) => {
      instanceIndexByGroupLink[groupLink] = instanceIndex;
    });

    const dependentResponseItems = itemEnablement.dependentItemLinks.map(
      responseItemLink => {
        let responseItemPath = this._surveyTree.getPathOfItem(responseItemLink);

        // conditions referencing a question inside the same repeating group
        // instance(s) resolve within those instances
        if (instanceRefs.length > 0) {
          responseItemPath = responseItemPath.map(pathSegment =>
            pathSegment in instanceIndexByGroupLink
              ? buildInstanceSegment(
                  pathSegment,
                  instanceIndexByGroupLink[pathSegment],
                )
              : pathSegment,
          );
        }

        const targetResponseItem =
          surveyResponse.getItemAtPath(responseItemPath);

        if (targetResponseItem !== null) return targetResponseItem;

        // response item is not on survey response yet init empty one
        const dependentSurveyItem =
          this._surveyTree.getSurveyItemWithLink(responseItemLink);
        return ResponseItem.initEmptyResponseItem(
          dependentSurveyItem.link,
          dependentSurveyItem.responseValueType,
        );
      },
    );

    return itemEnablement.isEnabled(dependentResponseItems);
  }

  /**
   * Initialize a SurveyResponse from a fhir questionnaire response
   * @param fhirQuestionnaireResponse - the starting fhir questionnaire response.  If no questionnaire response is provided, a default is generated
   * @param repeatingSurveyItems - the linkIDs of all repeating survey items.  this enables initialization of the array values
   * @param autopopulateValues - values to autopopulate in the survey.  note that this differs from initial values because autopopulate values are dynamic based on the context (patient or patient data) and initial values are statically configured on the questionnaire
   * @returns
   */
  defaultResponseWithInitialValues(
    fhirQuestionnaireResponse: fhir.QuestionnaireResponse | null = null,
    repeatingSurveyItems = this.allRepeatingItems,
    autopopulateValues?: FieldValueUpdate[],
  ): SurveyResponse {
    const allLinksInSurvey = this._surveyTree.allLinks;
    const surveyItemsWithInitialValues = allLinksInSurvey
      .map(link => {
        const targetItem = this._surveyTree.getSurveyItemWithLink(link);
        return targetItem;
      })
      .filter(surveyItem => {
        return surveyItem.hasInitialValue;
      });

    const responseNotStarted =
      !fhirQuestionnaireResponse ||
      SurveyResponse.isQuestionnaireResponseNotStarted(
        fhirQuestionnaireResponse,
      );
    const surveyResponse = SurveyResponse.initFromFhirQuestionnaireResponse(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      fhirQuestionnaireResponse,
      repeatingSurveyItems,
      this.allRepeatingGroupItems,
    );

    const executeFieldUpdates = (
      surveyResponse: SurveyResponse,
      fieldUpdates?: FieldValueUpdate[],
      ignoreNonExistingValues?: boolean,
    ): SurveyResponse => {
      if (!fieldUpdates?.length) return surveyResponse;

      if (!responseNotStarted) {
        console.error(
          'Attempted to set initial values on a survey response that has already been started.',
        );
        return surveyResponse;
      }

      const responseUpdates = this.fieldUpdatesToResponseUpdates(
        fieldUpdates,
        ignoreNonExistingValues,
      );
      return surveyResponse.updateFields(responseUpdates);
    };

    const initialValueFieldUpdates = surveyItemsWithInitialValues.map(
      surveyItem => {
        return new FieldValueUpdate(
          surveyItem.link,
          surveyItem.responseValueType,
          surveyItem.initialValue,
        );
      },
    );
    // Dynamic initial values: for now only support date fields with dynamic mode
    const dynamicInitialUpdates = allLinksInSurvey.reduce<FieldValueUpdate[]>(
      (acc, link) => {
        const surveyItem = this._surveyTree.getSurveyItemWithLink(link);
        // Extension parsing may throw (malformed modifierExtension),
        // but we want to prevent initialization from failing
        try {
          const extensionCollection =
            ExtensionCollection.initFromBackboneElement(
              surveyItem.fhirQuestionnaireItem,
            );
          const mode = extensionCollection.getValueString(
            surveyUrls.initialValueDynamic.mode,
          );
          if (!!mode && surveyItem.type === 'date') {
            // local timezone date of response at initialization time
            const today = dateParser.fromInputValue(moment());
            acc.push(
              new FieldValueUpdate(
                surveyItem.link,
                surveyItem.responseValueType,
                today,
              ),
            );
          }
        } catch (e) {
          reportErrorToBugsnagDirect(e as Error, {
            section: 'Survey.dynamicInitialUpdates',
            link,
          });
        }
        return acc;
      },
      [],
    );
    return [
      {
        updates: initialValueFieldUpdates,
        ignoreNonExistingValues: false,
      },
      { updates: dynamicInitialUpdates, ignoreNonExistingValues: true },
      { updates: autopopulateValues, ignoreNonExistingValues: true },
    ].reduce(
      (response, { updates, ignoreNonExistingValues }) =>
        executeFieldUpdates(response, updates, ignoreNonExistingValues),
      surveyResponse,
    );
  }

  /**
   * Formik requires all fields be specified in initial values.  Forms need to "touch" all fields on submission, formik needs to know which fields to touch.
   * This merges the defualt-populated values with remaining empty values
   *
   * @param {string} rootLink - optional.  Only required if paginated form
   * @param {Object} [defaultValues={}]
   * @memberof Survey
   */
  getInitialValues(
    surveyResponse: SurveyResponse,
    rootLink?: string,
  ): Record<string, any> {
    let defaultValues: Record<string, any> = {};
    if (surveyResponse && surveyResponse.getDefaultFormValues) {
      defaultValues = surveyResponse.getDefaultFormValues();
    }

    const repeatingGroupLinks = this.allRepeatingGroupItems;
    const isInsideRepeatingGroup = (leafLink: string) =>
      this._surveyTree
        .getPathOfItem(leafLink)
        .some(
          pathSegment =>
            pathSegment !== leafLink &&
            repeatingGroupLinks.includes(pathSegment),
        );

    // the nearest repeating-group ancestor of an item (excluding itself), or
    // null when the item is not nested inside another repeating group
    const nearestRepeatingAncestor = (link: string): string | null => {
      const path = this._surveyTree.getPathOfItem(link);
      for (let i = path.length - 2; i >= 0; i--) {
        if (repeatingGroupLinks.includes(path[i])) return path[i];
      }
      return null;
    };

    const emptyInitialValues: { [id: string]: any } = {};
    this._surveyTree
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      .getSubLeafLinks(rootLink)
      // leaves inside repeating groups are seeded per instance below
      .filter(leafLink => !isInsideRepeatingGroup(leafLink))
      .forEach(leafLink => {
        emptyInitialValues[leafLink] = null;
      });

    // Repeating groups seed one value object per answered instance (padded to
    // the group's minimum instance count) so every rendered field is known to
    // formik.  Nested repeating groups seed recursively: each instance value
    // object carries an array under the nested group's linkId.
    const seedGroupInstances = (
      groupLink: string,
      answeredInstances: Record<string, any>[],
    ): Record<string, any>[] => {
      const groupItem = this._surveyTree.getSurveyItemWithLink(groupLink);
      const groupPath = this._surveyTree.getPathOfItem(groupLink);

      // direct leaves: under this group but not under a nested repeating group
      const directLeafLinks = this._surveyTree
        .getSubLeafLinks(groupPath[0])
        .filter(leafLink => {
          const leafPath = this._surveyTree.getPathOfItem(leafLink);
          return (
            leafPath.includes(groupLink) &&
            nearestRepeatingAncestor(leafLink) === groupLink
          );
        });

      const nestedGroupLinks = repeatingGroupLinks.filter(
        nestedLink => nearestRepeatingAncestor(nestedLink) === groupLink,
      );

      const emptyInstanceSeed: Record<string, any> = {};
      directLeafLinks.forEach(leafLink => {
        emptyInstanceSeed[leafLink] = null;
      });

      const instanceCount = Math.max(
        answeredInstances.length,
        groupItem.minInstanceCount,
      );

      const seededInstances: Record<string, any>[] = [];
      for (
        let instanceIndex = 0;
        instanceIndex < instanceCount;
        instanceIndex++
      ) {
        const answered = answeredInstances[instanceIndex] || {};
        const instanceSeed: Record<string, any> = {
          ...emptyInstanceSeed,
          ...answered,
        };
        nestedGroupLinks.forEach(nestedLink => {
          instanceSeed[nestedLink] = seedGroupInstances(
            nestedLink,
            isArray(answered[nestedLink]) ? answered[nestedLink] : [],
          );
        });
        seededInstances.push(instanceSeed);
      }
      return seededInstances;
    };

    repeatingGroupLinks
      // nested groups are seeded within their parent's instances
      .filter(groupLink => nearestRepeatingAncestor(groupLink) === null)
      .forEach(groupLink => {
        const groupPath = this._surveyTree.getPathOfItem(groupLink);
        if (rootLink && groupPath[0] !== rootLink) return;

        const answeredInstances: Record<string, any>[] = isArray(
          defaultValues[groupLink],
        )
          ? defaultValues[groupLink]
          : [];
        defaultValues[groupLink] = seedGroupInstances(
          groupLink,
          answeredInstances,
        );
      });

    return {
      ...emptyInitialValues,
      ...defaultValues,
    };
  }

  /**
   * Gets the choice options for a choice item time.  Warning will be logged if not called for an item type
   *
   * @param {string} itemLink
   * @returns {ChoiceOptions}
   * @memberof Survey
   */
  getChoiceOptionsForItem(itemLink: string): ChoiceOptions {
    // surveys are immutable so choice options should never change, cache to avoid costly generation
    if (this._choiceOptionsCache[itemLink]) {
      return this._choiceOptionsCache[itemLink];
    }

    const targetItem = this._surveyTree.getSurveyItemWithLink(itemLink);

    if (
      !targetItem ||
      (targetItem.type !== 'choice' && targetItem.type !== 'open-choice')
    ) {
      console.warn(
        'Attempted to get choice options on a non-choice survey item',
      );
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return null;
    }

    let choiceOptions = null;
    if (targetItem.optionReference) {
      const targetValueSet = this.valueSets.getValueSetWithId(
        targetItem.optionReference,
      );
      if (!targetValueSet) {
        console.error(
          'Attempted to retrieve choice options for a non-existent valueset',
        );
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return null;
      }

      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      choiceOptions = ChoiceOptions.initWithValueSet(targetValueSet);
    } else if (targetItem.hasConfiguredOptions) {
      // @ts-ignore This directive was added as part of a bulk-change to enable strictNullChecks.
      choiceOptions = ChoiceOptions.initWithConfiguredOption(
        targetItem.optionList,
      );
    } else {
      console.error(
        'Attempted to get choice options on a survey item without configured options or a value set reference',
      );
    }

    this._choiceOptionsCache[itemLink] = choiceOptions;
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return choiceOptions;
  }

  /**
   * Appends a new questionnaire item after the target link and returns a cloned survey
   * @param questionnaireItem
   */
  appendItemAfterLink(
    questionnaireItem: fhir.QuestionnaireItem,
    link: string,
  ): Survey {
    const pathToLinkedItem = this._surveyTree.getPathOfItem(link);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.appendItemAfter(
      pathToLinkedItem,
      questionnaireItem,
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Appends new questionnaire items after the target link and returns a cloned survey
   * @param questionnaireItem
   */
  appendItemsAfterLink(
    questionnaireItems: fhir.QuestionnaireItem[],
    link: string,
  ): Survey {
    if (!questionnaireItems?.length) return this;

    return questionnaireItems.reduce(
      (acc, questionnaireItem) => {
        return {
          survey: acc.survey.appendItemAfterLink(
            questionnaireItem,
            acc.lastUsedLink,
          ),
          lastUsedLink: questionnaireItem.linkId,
        };
      },
      {
        lastUsedLink: link,
        survey: this as Survey,
      },
    ).survey;
  }

  /**
   * Replaces the sub items of the target survey item
   */
  setChildItem(
    questionnaireItem: fhir.QuestionnaireItem,
    link: string,
  ): Survey {
    const pathToLinkedItem = this._surveyTree.getPathOfItem(link);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.setChildItem(
      pathToLinkedItem,
      questionnaireItem,
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Set all the specified items as chilrem
   */
  setChildItems(
    questionnaireItems: fhir.QuestionnaireItem[],
    link: string,
  ): Survey {
    if (!questionnaireItems?.length) return this;

    const [first, ...rest] = questionnaireItems;

    const withFirstChildSet = this.setChildItem(first, link);
    if (!rest?.length) return withFirstChildSet;

    return withFirstChildSet.appendItemsAfterLink(rest, first.linkId);
  }

  /**
   * Replaces the target survey item
   */
  replaceItem(questionnaireItem: fhir.QuestionnaireItem, link: string): Survey {
    const pathToLinkedItem = this._surveyTree.getPathOfItem(link);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.replaceItem(
      pathToLinkedItem,
      questionnaireItem,
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * replace all items and return a cloned survey
   */
  replaceItems(questionnaireItems: fhir.QuestionnaireItem[]): Survey {
    if (!questionnaireItems || !questionnaireItems.length) return this;

    return questionnaireItems
      .filter(item => {
        if (!item || !item.linkId) {
          console.error(
            'Given an update questionnaire item without a link Id.  Skipping update.',
          );
          return false;
        }

        return true;
      })
      .reduce(
        (acc: Survey, questionnaireItem) =>
          acc.replaceItem(questionnaireItem, questionnaireItem.linkId),
        this,
      );
  }

  /**
   * Given a dependency check result, executes an update on the dependent items if necessary
   */
  executeDependencyUpdates(
    dependencyCheckResult: DependencyCheckResult,
  ): Survey {
    if (dependencyCheckResult.checkStatus === 'unresolvable') {
      console.error(
        'Attempted to execute dependency updates on unresolvable dependencies.  Not executing update.',
      );
      return this;
    } else if (dependencyCheckResult.checkStatus === 'not-affected') {
      return this;
    }

    const dependentItems = (dependencyCheckResult.logicResults || [])
      .filter(
        logicResult =>
          logicResult && logicResult.checkStatus === 'needs-update',
      )
      .map(
        logicResult =>
          logicResult.updatedSurveyItem &&
          logicResult.updatedSurveyItem.fhirQuestionnaireItem,
      )
      .filter(Boolean);

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return this.replaceItems(dependentItems);
  }

  /**
   * Does a dependency check on the desired replacement operation
   */
  replaceItemDependencyCheck(
    questionnaireItem: fhir.QuestionnaireItem,
  ): DependencyCheckResult {
    if (!questionnaireItem || !questionnaireItem.linkId) {
      console.error(
        'Tried to do a replace item dependency check on an invalid item',
      );
      return SurveyDependencies.notAffectedResult;
    }

    const original = this._surveyTree.getSurveyItemWithLink(
      questionnaireItem.linkId,
    );

    if (!original) {
      console.error(
        `Tried to do a replace with a questionnaire item when the original item could not be found.  Targeted original think id: "${questionnaireItem.linkId}"`,
      );
      return SurveyDependencies.notAffectedResult;
    }

    return this._surveyDependencies.checkUpdate(
      original,
      new SurveyItem(questionnaireItem, original.isRootItem),
    );
  }

  /**
   * Replaces the target survey item
   */
  updateItemText(newText: string, link: string): Survey {
    const pathToLinkedItem = this._surveyTree.getPathOfItem(link);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.updatePropertiesOnItem(
      pathToLinkedItem,
      { text: newText },
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Swaps the two sibling items with given links
   */
  swapSiblingItems(link1: string, link2: string): Survey {
    const pathToItem1 = this._surveyTree.getPathOfItem(link1);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.swapItems(
      pathToItem1,
      link2,
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Deletes the item with the given link
   */
  deleteItem(link: string): Survey {
    const itemPath = this._surveyTree.getPathOfItem(link);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater.removeItem(itemPath).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Does a dependency check on the desired deletion operation
   */
  deleteItemDependencyCheck(link: string): DependencyCheckResult {
    // deletion item can have items below it
    const subLinks = this._surveyTree.getSubLinks(link);
    const allLinks = [link, ...subLinks];
    const allDependencyChecks = allLinks
      .filter(Boolean)
      .map(link => this._surveyTree.getSurveyItemWithLink(link))
      .filter(Boolean)
      .map(item =>
        // make sure to not check deletion dependency of subItem1 depending on subItem2
        this._surveyDependencies.checkRemoval(item, allLinks),
      );

    return SurveyDependencies.combineDependencyResults(allDependencyChecks);
  }

  /**
   * If multiple items have to be deleted/updated at once, the updates cannot happen isolation.  The result of
   * one update may have a result on another update.  This method will chain updates and return the aggregated dependency result
   * along with a copied survey with the action executed
   * @param replaceItems - The survey items to replace
   * @param deletedLinkIds - The suvey items to delete
   */
  runSequentialDependencyChecks(
    replaceItems: fhir.QuestionnaireItem[],
    deletedLinkIds: string[],
  ) {
    const startDependencyResult: DependencyCheckResult = {
      checkStatus: 'not-affected',
      logicResults: [],
    };
    const start = {
      survey: this as Survey,
      dependencyResult: startDependencyResult,
    };
    const postReplace = (
      replaceItems || ([] as fhir.QuestionnaireItem[])
    ).reduce((acc, item) => {
      const { survey, dependencyResult } = acc;

      // if previously encountered an unresolvable dependency, bail
      if (dependencyResult.checkStatus === 'unresolvable') return acc;

      const replaceDependencyRes = survey.replaceItemDependencyCheck(item);
      return {
        survey: survey
          .executeDependencyUpdates(replaceDependencyRes)
          .replaceItem(item, item.linkId),
        dependencyResult: SurveyDependencies.combineDependencyResults([
          dependencyResult,
          replaceDependencyRes,
        ]),
      };
    }, start);

    return (deletedLinkIds || ([] as string[])).reduce((acc, linkId) => {
      const { survey, dependencyResult } = acc;

      // if previously encountered an unresolvable dependency, bail
      if (dependencyResult.checkStatus === 'unresolvable') return acc;

      const replaceDependencyRes = survey.deleteItemDependencyCheck(linkId);
      return {
        survey: survey
          .executeDependencyUpdates(replaceDependencyRes)
          .deleteItem(linkId),
        dependencyResult: SurveyDependencies.combineDependencyResults([
          dependencyResult,
          replaceDependencyRes,
        ]),
      };
    }, postReplace);
  }

  /**
   * Can this item be moved up in reference to its siblings
   */
  canMoveItemUp(link: string): boolean {
    return !this._surveyTree.getIsFirstSibling(link);
  }

  /**
   * Can this item be moved down in reference to its siblings
   */
  canMoveItemDown(link: string): boolean {
    return !this._surveyTree.getIsLastSibling(link);
  }

  /**
   * Does the link have any sibling survey items
   */
  doesItemHaveSiblings(link: string): boolean {
    return !this._surveyTree.getHasNoSibligs(link);
  }

  /**
   * Swaps the target item with the sibling right before it
   */
  moveItemUp(link: string): Survey {
    if (!this.canMoveItemUp(link)) {
      console.error(
        `Attempted to move item with link "${link}" but is not able move up`,
      );
      return this;
    }

    const siblingLinks = this._surveyTree.getSiblingLinks(link);
    const linkIndex = siblingLinks.indexOf(link);
    const swapLink = siblingLinks[linkIndex - 1];
    return this.swapSiblingItems(link, swapLink);
  }

  /**
   * Swaps the target item with the sibling right before it
   */
  moveItemDown(link: string): Survey {
    if (!this.canMoveItemDown(link)) {
      console.error(
        `Attempted to move item with link "${link}" but is not able move down`,
      );
      return this;
    }

    const siblingLinks = this._surveyTree.getSiblingLinks(link);
    const linkIndex = siblingLinks.indexOf(link);
    const swapLink = siblingLinks[linkIndex + 1];
    return this.swapSiblingItems(link, swapLink);
  }

  /**
   * All the survey items of input controls
   */
  get controlSurveyItems(): SurveyItem[] {
    return this._surveyTree.allItems.filter(_ => _.isControl);
  }

  /**
   * Appends a new survey builder default page onto a cloned survey.  Returns the clone
   */
  apendNewBuilderPage(pageItem: fhir.QuestionnaireItem): Survey {
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    // check if any pages are currently present
    if (this._items.length === 0) {
      // set the initial page but maintain other questionnaire metadata (title, description)
      updater.setRootQuestionnaireItem([pageItem]);
    } else {
      // append new page to the end
      const lastPageLink = this._items[this._items.length - 1].link;
      const itemPath = this._surveyTree.getPathOfItem(lastPageLink);
      updater.appendItemAfter(itemPath, pageItem);
    }

    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Clones the target survey item and appends it to the survey as the last sibling of the target link
   * @param linkId
   */
  cloneSurveyItem(linkId: string): Survey {
    const targetSurveyItem = this._surveyTree.getSurveyItemWithLink(linkId);

    if (!targetSurveyItem) {
      console.error('Attempted to clone a non-existent survey item');
      return this;
    }

    const targetItemPath = this._surveyTree.getPathOfItem(linkId);
    const itemSiblings = this._surveyTree.getSiblingLinks(linkId);

    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    updater.cloneItemAndAppend(
      targetItemPath,
      itemSiblings,
      targetSurveyItem.fhirQuestionnaireItem,
    );
    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Is updates to the target survey item disabled due to versioning
   * @param link - the target survey item to check if items are enabled
   */
  areItemUpdatesDisabled(linkId: string): boolean {
    const targetItem = this.getSurveyItemWithLinkId(linkId);

    if (!targetItem) {
      console.error(`Cannot find survey item with link ID "${linkId}"`);
      return false;
    }

    return targetItem.isUpdateForbidden;
  }

  /**
   * Updates the survey metadata.  Returns the clone
   */
  updateMetadata(title: string = '', description: string = ''): Survey {
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire = updater
      .updateTitle(title)
      .updateDescription(description).fhirQuestionnaire;

    return new Survey(updatedQuestionnaire);
  }

  /**
   * Update all custom message types associate with the survey
   */
  updateCustomMessages(
    messages: Record<CustomMessages, string | undefined>,
  ): Survey {
    const updatedQuestionnaire = allCustomMessageTypes.reduce(
      (updater, customMessageType) => {
        const message = messages[customMessageType];
        return updater.updateCustomMessage(customMessageType, message);
      },
      new QuestionnaireUpdater(this._fhirQuestionnaire),
    ).fhirQuestionnaire;

    return new Survey(updatedQuestionnaire);
  }

  /**
   * Update all custom message types associate with the survey
   */
  getCustomMessage(messageType: CustomMessages): string | null {
    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this._fhirQuestionnaire,
    );
    const messageExtensionUrl = surveyUrls.customMessage(messageType);
    if (!extensionCollection.hasExtension(messageExtensionUrl)) return null;

    return extensionCollection.getValueString(messageExtensionUrl) || null;
  }

  /**
   * Checks if value set name already exists.  This checks derived IDs of valuesets
   */
  doesValueSetNameExist(name: string) {
    return this._valueSets.doesNameAlreadyExist(name);
  }

  createNewValueSet(valueSetInput: CreateValueSetInput): Survey {
    if (
      isNil(valueSetInput) ||
      isNil(valueSetInput.codes) ||
      isNil(valueSetInput.name)
    ) {
      console.error(
        'Unable to create a new survey value set.  Provided insufficient value set data',
      );
      return this;
    }

    const isValidName = ValueSet.isValidValueSetName(valueSetInput.name);
    const doesNameAlreadyExist = this.doesValueSetNameExist(valueSetInput.name);

    if (!isValidName || doesNameAlreadyExist) {
      console.error('Unable to create value set.  Name is invalid.');
      return this;
    }

    const validCodes = isArray(valueSetInput.codes)
      ? valueSetInput.codes.filter(_ => !!_.display)
      : [];

    if (!validCodes.length) {
      console.error('Unable to create value set.  Code set is invalid.');
      return this;
    }

    const id = ValueSet.generateIdFromName(valueSetInput.name);
    const systemUrl = surveyUrls.getSurveyValueSetSystem(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      this._fhirQuestionnaire.id,
      id,
    );
    const dedupedCodes = uniqBy(
      valueSetInput.codes,
      _ => `${_.display}|${_.code}`,
    );
    const codings = dedupedCodes.map(code =>
      Coding.initFromFormValue(
        code,
        valueSetInput?.system || systemUrl,
        valueSetInput.enableNumericValues,
        valueSetInput.purpose,
      ),
    );
    const valueSet = new ValueSet(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      id,
      valueSetInput.name,
      valueSetInput.purpose,
      codings,
    );
    const updatedValuesSets = this._valueSets.cloneAndAddValueSet(valueSet);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire =
      updater.updateValueSets(updatedValuesSets).fhirQuestionnaire;

    return new Survey(updatedQuestionnaire);
  }

  /**
   * Will bring in a value set from a different survey.  This attempts to reconcile naming conflicts
   */
  importSurveyQuestion(
    fromSurvey: Survey,
    importItem: SurveyItem,
    importToPageId: string,
  ): ImportSurveyQuestionResult {
    try {
      if (!importItem?.isSurveyBuilderQuestion) {
        // should never be called under these circumstances, but check again
        return { status: 'invalid-item' };
      }

      if (this.getSurveyItemWithLinkId(importItem.link)) {
        return { status: 'item-already-exists' };
      }

      let updatedSurvey: Survey | null = null;
      let importQuestionnaireItem = importItem.importedVersionOfItem;

      // ensure this question doesn't already exist

      // importing a survey question means the survey item's valueset must also be imported
      if (importItem.hasOptionReference) {
        const valueSetToImport = fromSurvey.valueSets.getValueSetWithId(
          importItem.optionReference,
        );
        const addValueSetResult =
          this.valueSets.addExternalValueSet(valueSetToImport);

        // update this survey with the new value sets
        const questionnaireUpdater = new QuestionnaireUpdater(
          this._fhirQuestionnaire,
        );
        const updatedQuestionnaire = questionnaireUpdater.updateValueSets(
          addValueSetResult.updatedValueSets,
        ).fhirQuestionnaire;
        updatedSurvey = new Survey(updatedQuestionnaire);

        // the value set ID may have been updated to avoid ID conflicts, update the imported item option reference
        importQuestionnaireItem = new QuestionnaireItemUpdater(
          importItem.fhirQuestionnaireItem,
        ).setOptionReference(
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          addValueSetResult.ingestedValueSetId,
        ).questionnaireItem;
      }

      // add the imported item
      updatedSurvey = (updatedSurvey || this).addItemsToPage(importToPageId, [
        importQuestionnaireItem,
      ]);

      return {
        updatedSurvey,
        status: 'success',
      };
    } catch (e) {
      console.error(e);
      return { status: 'unkown-error' };
    }
  }

  /**
   * Deletes the target value set.  If this is a system value set or the value set has references to it
   * an error will be logged and the deletion will be aborted
   */
  deleteValueSet(valueSetId: string): Survey {
    const referenceCount = this.getValueSetReferenceCount(valueSetId);
    if (referenceCount) {
      console.error(
        'Unable to delete a value set that is currently being referenced elsewhere within the survey',
      );
      return this;
    }

    const isSystemValueSet = this._valueSets.isSystemValueSet(valueSetId);
    if (isSystemValueSet) {
      console.error('Unable to delete a non-user-created value set');
      return this;
    }

    const updatedValueSets = this._valueSets.cloneAndRemoveValueSet(valueSetId);
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const updatedQuestionnaire =
      updater.updateValueSets(updatedValueSets).fhirQuestionnaire;

    return new Survey(updatedQuestionnaire);
  }

  /**
   * Will add the item to the end of the specified page
   */
  addItemsToPage(pageLinkId: string, items: fhir.QuestionnaireItem[]) {
    const lastItemOnPage = this.getLastItemOnPage(pageLinkId);
    return !lastItemOnPage
      ? this.setChildItems(items, pageLinkId)
      : this.appendItemsAfterLink(items, lastItemOnPage.link);
  }

  /**
   * Returns the count of the number of survey items currently using this value set
   */
  getValueSetReferenceCount(valueSetId: string): number {
    return this._valueSets.getValueSetReferenceCount(
      valueSetId,
      this._surveyTree.allItems,
    );
  }

  /**
   * Returns a survey clone with the cloned survey in a published state
   */
  setStatus(status: SurveyStatus): Survey {
    if (status === 'draft') {
      console.warn(
        'Transitioning to a draft status on a survey can corrupt existing responses.',
      );
    }

    const updatedQuestionnaire = new QuestionnaireUpdater(
      this._fhirQuestionnaire,
    ).setSurveyStatus(status).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Returns a survey clone with the cloned survey in a published state
   */
  setInternalStatus(isInternalSurvey: boolean): Survey {
    const updatedQuestionnaire = new QuestionnaireUpdater(
      this._fhirQuestionnaire,
    ).setInternalStatus(isInternalSurvey).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  setAllowsResponseResubmission(allowsResponseResubmission: boolean): Survey {
    const updatedQuestionnaire = new QuestionnaireUpdater(
      this._fhirQuestionnaire,
    ).setAllowsResponseResubmission(
      allowsResponseResubmission,
    ).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  /**
   * Toggles the survey-level repeating-groups opt-in flag (see
   * isRepeatingGroupsEnabled).
   */
  setRepeatingGroupsEnabled(enabled: boolean): Survey {
    const updatedQuestionnaire = new QuestionnaireUpdater(
      this._fhirQuestionnaire,
    ).setRepeatingGroupsEnabled(enabled).fhirQuestionnaire;
    return new Survey(updatedQuestionnaire);
  }

  setAllowsMemberResponsePdfVisibility(isAllowed: boolean): Survey {
    let updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    if (isAllowed) {
      updater = updater.upsertTag({ system: surveyUrls.vendorId });
    } else {
      updater = updater.removeTag(surveyUrls.vendorId);
    }
    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Get all child links starting directly below the given parent link
   */
  getAllChildLinks(parentLink: string): string[] {
    return this._surveyTree.getSubLinks(parentLink);
  }

  /**
   * Get all child links of controls starting directly below given parent link
   */
  getAllControlChildLInks(parentLink: string): string[] {
    const allChildLInks = this.getAllChildLinks(parentLink) || [];
    return allChildLInks.filter(childLink => {
      const childItem = this.getSurveyItemWithLinkId(childLink);
      return childItem && childItem.isControl;
    });
  }

  /**
   * This restricts future updates to applicable survey items.
   * Any survey item that survey service will generate downstream data for will get a tag that prevents any modification.
   * This ensures that all properties of the survey item will remain constant and all generated data will mantain integrity
   * and continuity.
   */
  getVersionedSurvey(): Survey {
    const lockDownModifierExtension: fhir.Extension = {
      url: surveyUrls.updatesForbidden,
    };

    // allow genomics-report to be modified
    const itemsToLockDown = this.allItems.filter(_ => _.disableVersionedEdits);
    const lockDownReduction = (
      updater: QuestionnaireUpdater,
      lockDownItem: SurveyItem,
    ): QuestionnaireUpdater => {
      const pathToItem = this._surveyTree.getPathOfItem(lockDownItem.link);
      return updater.addModifierExtensionToItem(
        pathToItem,
        lockDownModifierExtension,
      );
    };

    const resultingUpdater = itemsToLockDown
      .reduce(
        lockDownReduction,
        new QuestionnaireUpdater(this._fhirQuestionnaire),
      )
      .setSurveyStatus('draft');

    return new Survey(resultingUpdater.fhirQuestionnaire);
  }

  /**
   * Returns the path to the target link ID
   */
  getPathToItem(targetItemLinkId: string): string[] {
    return this._surveyTree.getPathOfItem(targetItemLinkId);
  }

  /**
   * Returns the links of all the survey pages
   */
  getPageLinks(): string[] {
    return this._surveyTree.rootSurveyItems().map(item => item.link);
  }

  /**
   * Determines if the specified scoring algorithm is configured to be applied to the root of the survey
   */
  isScoringSurvey(scoringFunction: 'sum' | 'avg'): boolean {
    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this._fhirQuestionnaire,
    );
    const targetExtensionUrl =
      scoringFunction === 'sum'
        ? surveyUrls.sumScoreSection
        : surveyUrls.avgScoreSection;
    return extensionCollection.hasExtension(targetExtensionUrl);
  }

  /**
   * Determines if this survey contains ANY configured scoring for either the root of the survey or any survey sections
   */
  get surveyContainsScoredSections(): boolean {
    if (this.isScoringSurvey('sum') || this.isScoringSurvey('avg')) return true;

    return !!this.surveyItems.find(_ => _.isScoring);
  }

  /**
   * Will toggle survey scoring of the specified scoring function
   */
  toggleSurveyScoring(scoringFunction: 'sum' | 'avg'): Survey {
    const targetExtensionUrl =
      scoringFunction === 'sum'
        ? surveyUrls.sumScoreSection
        : surveyUrls.avgScoreSection;
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    updater.removeSurveyModifierExtension(targetExtensionUrl);
    const scoringCurrentlyOn = this.isScoringSurvey(scoringFunction);
    if (!scoringCurrentlyOn) {
      updater.addSurveyModifierExtension({
        url: targetExtensionUrl,
      });
    }

    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Determines if this survey has opted into rendering repeating groups
   * (`repeats: true` on group items) as instance cards with an "Add another"
   * button.  Surveys without the extension render such groups with the
   * legacy single-section behavior.
   */
  isRepeatingGroupsEnabled(): boolean {
    // called from allRepeatingItems / allRepeatingGroupItems on hot paths
    // like defaultResponseWithInitialValues; a malformed extension list must
    // fall back to the feature being off, not break response initialization
    try {
      const extensionCollection = ExtensionCollection.initFromBackboneElement(
        this._fhirQuestionnaire,
      );

      return extensionCollection.hasExtension(surveyUrls.enableRepeatingGroups);
    } catch (e) {
      return false;
    }
  }

  /**
   * Determines if the specified scoring algorithm is configured to be applied to the root of the survey
   */
  isShowingScore(): boolean {
    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this._fhirQuestionnaire,
    );

    return extensionCollection.hasExtension(surveyUrls.showScore);
  }

  /**
   * Will toggle showing the score of the survey to the users
   */
  toggleShowScore(): Survey {
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const isShowingScore = this.isShowingScore();
    if (isShowingScore) {
      updater.removeSurveyModifierExtension(surveyUrls.showScore);
    } else {
      updater.addSurveyModifierExtension({
        url: surveyUrls.showScore,
      });
    }

    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Will toggle enabling the retake survey button
   */
  isRetakeSurveyEnabled(): boolean {
    const extensionCollection = ExtensionCollection.initFromBackboneElement(
      this._fhirQuestionnaire,
    );

    return extensionCollection.hasExtension(surveyUrls.enableRetakeSurvey);
  }

  /**
   * Will toggle enabling retaking the survey
   */
  toggleRetakeSurvey(): Survey {
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    const isRetakeEnabled = this.isRetakeSurveyEnabled();
    if (isRetakeEnabled) {
      updater.removeSurveyModifierExtension(surveyUrls.enableRetakeSurvey);
    } else {
      updater.addSurveyModifierExtension({
        url: surveyUrls.enableRetakeSurvey,
      });
    }

    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * Adds a primary survey ID to the survey.
   * Note that this is handled by the API but can be useful to keep a client-side cache up-to-date
   */
  upsertPrimarySurveyId(primarySurveyId: string): Survey {
    const updater = new QuestionnaireUpdater(this._fhirQuestionnaire);
    updater.upsertTag({
      system: surveyUrls.primarySurveyId,
      code: primarySurveyId,
    });

    return new Survey(updater.fhirQuestionnaire);
  }

  /**
   * All the scoring survey items
   */
  get scoredGroups(): SurveyItem[] {
    return this.allItems.filter(_ => _.isScoring);
  }

  /**
   * Gets the raw scoring value of a singular response item.
   * These values are what are eventually aggregated into sum
   * and avg scores of a survey group
   */
  getResponseItemScoringValue(responseItem: ResponseItem): number[] {
    const sourceSurveyItem = this.getSurveyItemWithLinkId(responseItem.link);
    const isOmittedFromScoring = this.getPathToItem(responseItem.link).find(
      _ => this.getSurveyItemWithLinkId(_).isMarkedForScoringOmission,
    );

    if (isOmittedFromScoring) return [];

    return sourceSurveyItem.getScoringValue(
      this.valueSets,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      responseItem.toFhirResponseItem().answer,
    );
  }

  private _gatherGroupResponseItemsScores(
    groupResponseItem: ResponseItem,
  ): number[] | null {
    const sourceSurveyItem = this.getSurveyItemWithLinkId(
      groupResponseItem.link,
    );
    if (!sourceSurveyItem) return null;

    const allNestedItems = groupResponseItem.allTreeResponseItems;
    const rawIndividualScores = allNestedItems.map(_ =>
      this.getResponseItemScoringValue(_),
    );
    return flatten(rawIndividualScores.filter(_ => _?.length));
  }

  /**
   * Score a group response item.
   *
   * It will recursively walk down the survey tree gathering
   * applicalbe scores and then generate (if applicable) the
   * avg and sum scores of children response item
   */
  scoreGroupResponseItem(groupResponseItem: ResponseItem): ScoreResult | null {
    if (!groupResponseItem?.link) return null;

    const sourceSurveyItem = this.getSurveyItemWithLinkId(
      groupResponseItem.link,
    );

    if (!sourceSurveyItem || !sourceSurveyItem.isScoring) return null;

    const individualScores =
      this._gatherGroupResponseItemsScores(groupResponseItem);
    return {
      avg: sourceSurveyItem.isAverageScoring ? mean(individualScores) : null,
      sum: sourceSurveyItem.isSumScoring ? sum(individualScores) : null,
    };
  }

  /**
   * Score a survey.
   *
   * It will score all pages of the survey if total survey scoring is enabled.
   */
  scoreSurvey(response: SurveyResponse): ScoreResult | null {
    const scoreSum = this.isScoringSurvey('sum');
    const scoreAvg = this.isScoringSurvey('avg');
    if (!scoreSum && !scoreAvg) return null;

    const eachPageGroupItemScores = response.rootResponseItems
      .map(_ => this._gatherGroupResponseItemsScores(_))
      .filter(_ => !isNil(_));
    const allItemScores = flatten(eachPageGroupItemScores);
    return {
      avg: scoreAvg ? mean(allItemScores) : null,
      sum: scoreSum ? sum(allItemScores) : null,
    };
  }

  /**
   * Sorts the given link ids into the specified tree-traversal order
   * @param linkIds - the link Ids to sort
   * @param order - the order to sort the links.  "pre-order" puts a root node
   * before the children nodes.  "post-order" puts the root node after the children nodes.
   */
  sortLinkIds(linkIds: string[], order: 'pre-order' | 'post-order'): string[] {
    const sortIndexHash = this.allSurveyLinks.reduce<Record<string, number>>(
      (acc, linkId) => {
        return {
          ...acc,
          [linkId]:
            order === 'pre-order'
              ? this._surveyTree.getPreOrderIndexOfLink(linkId)
              : this._surveyTree.getPostOrderIndexOfLink(linkId),
        };
      },
      {},
    );
    return [...linkIds].sort((l1, l2) => sortIndexHash[l1] - sortIndexHash[l2]);
  }

  autopopulateResponseToFieldValueUpdates(
    response: AutopopulateResponse,
  ): FieldValueUpdate[] {
    return response
      .filter(_ => !isNil(_.value))
      .filter(_ => {
        const surveyItem = this.getSurveyItemWithLinkId(_.linkId);

        return surveyItem.typeCheckType;
      })
      .map(_ => {
        // best effort type casting
        const surveyItem = this.getSurveyItemWithLinkId(_.linkId);
        if (
          surveyItem.typeCheckType === 'string' &&
          (isBoolean(_.value) || isNumber(_.value))
        ) {
          return {
            ..._,
            value: String(_.value),
          };
        } else {
          return _;
        }
      })
      .filter(_ => {
        const surveyItem = this.getSurveyItemWithLinkId(_.linkId);

        const isValueValidType = Array.isArray(_.value)
          ? _.value.every(value => {
              const isValueValidType =
                !!surveyItem.typeCheckType &&
                fhirTypeCheck(surveyItem.typeCheckType, value, false);

              return isValueValidType;
            })
          : !!surveyItem.typeCheckType &&
            fhirTypeCheck(surveyItem.typeCheckType, _.value, false);

        return isValueValidType;
      })
      .map(_ => {
        const surveyItem = this.getSurveyItemWithLinkId(_.linkId);
        return new FieldValueUpdate(
          _.linkId,
          surveyItem.responseValueType,
          _.value,
        );
      });
  }
}
